import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Mail, X } from 'lucide-react'
import {
  authorizedFetch,
  Button,
  getAccessRequestConfig,
  type AccessRequestAdminPlugin,
  type AccessRequestRowActionsProps,
} from '@yeshuman/sdk'

/** Config key `signup.accessRequest.sendWorkingSession` turns on the working session door. */
export function workingSessionDoorEnabled(): boolean {
  return Boolean(getAccessRequestConfig().sendWorkingSession)
}

function useSendWorkingSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entryId: string): Promise<{ id: string; session_url: string; created: boolean }> => {
      const response = await authorizedFetch(
        `/api/working-sessions/admin/from-enquiry/${entryId}/send`,
        { method: 'POST' },
      )
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || error.detail || 'Failed to send session')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] })
    },
  })
}

function SendSessionActions({ entry, busy, reject, setError }: AccessRequestRowActionsProps) {
  const sendSession = useSendWorkingSession()
  if (entry.status !== 'pending' && entry.status !== 'session_sent') return null
  const disabled = busy || sendSession.isPending

  return (
    <div className="inline-flex items-center gap-2">
      <Button
        size="sm"
        onClick={() => {
          setError('')
          sendSession.mutate(entry.id, { onError: (error) => setError(error.message) })
        }}
        disabled={disabled}
      >
        <Mail className="h-4 w-4" aria-hidden />
        {entry.status === 'session_sent' ? 'Resend session' : 'Send session'}
      </Button>
      {entry.status === 'pending' ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={reject}
          disabled={disabled}
          aria-label={`Reject ${entry.email}`}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      ) : null}
    </div>
  )
}

export const workingSessionAdmin: AccessRequestAdminPlugin = {
  RowActions: SendSessionActions,
  description: 'Review enquiries. Send a working session when it is a fit. Reject junk.',
  hideBulkInvite: true,
}
